import { API } from '@/config/api';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNavigation from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import {
  MessageSquare, Send, Plus, Trash2, Loader2, Bot, User, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';

/** Simple markdown to JSX renderer */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  for (const line of lines) {
    i++;
    // Headings
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-slate-900 mt-4 mb-1.5 text-[14px]">{renderInline(line.slice(4))}</h4>);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-bold text-slate-900 mt-4 mb-2 text-[15px]">{renderInline(line.slice(3))}</h3>);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="font-bold text-slate-900 mt-4 mb-2 text-base">{renderInline(line.slice(2))}</h2>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={i} className="flex gap-2 ml-1 my-0.5">
          <span className="text-teal-500 mt-0.5 shrink-0">•</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="my-0.5">{renderInline(line)}</p>);
    }
  }
  return elements;
}

function renderInline(text) {
  // Bold **text**
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const idx = remaining.indexOf(boldMatch[0]);
      if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
      parts.push(<strong key={key++} className="font-semibold text-slate-900">{boldMatch[1]}</strong>);
      remaining = remaining.slice(idx + boldMatch[0].length);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return parts;
}

export default function AdminChat() {
  const confirmDialog = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch { setSessions([]); }
    finally { setLoadingSessions(false); }
  }, []);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const res = await axios.get(`${API}/chat/sessions/${sessionId}/messages`, { withCredentials: true });
      setMessages(res.data || []);
    } catch { setMessages([]); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => {
    if (activeSession) fetchMessages(activeSession);
    else setMessages([]);
  }, [activeSession, fetchMessages]);

  const createSession = async () => {
    try {
      const res = await axios.post(`${API}/chat/sessions`, {}, { withCredentials: true });
      setActiveSession(res.data.id);
      await fetchSessions();
      textareaRef.current?.focus();
    } catch { toast.error('Kunne ikkje opprette samtale'); }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    const ok = await confirmDialog({ title: 'Slette samtale?', message: 'Alle meldingar vert sletta.', confirmText: 'Slett', variant: 'danger' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/chat/sessions/${id}`, { withCredentials: true });
      if (activeSession === id) { setActiveSession(null); setMessages([]); }
      fetchSessions();
    } catch { toast.error('Kunne ikkje slette'); }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    let sessionId = activeSession;
    if (!sessionId) {
      try {
        const res = await axios.post(`${API}/chat/sessions`, {}, { withCredentials: true });
        sessionId = res.data.id;
        setActiveSession(sessionId);
        fetchSessions();
      } catch { toast.error('Kunne ikkje starte samtale'); return; }
    }

    // Optimistic: show user message immediately
    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [...prev, { id: tempId, role: 'user', content: text, created_at: new Date().toISOString() }]);
    setInput('');
    setSending(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      const res = await axios.post(`${API}/chat/send`, { session_id: sessionId, message: text }, { withCredentials: true });
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempId);
        return [...withoutTemp, { id: tempId.replace('temp', 'u'), role: 'user', content: text, created_at: new Date().toISOString() }, res.data.message];
      });
      fetchSessions();
    } catch {
      toast.error('Feil ved sending');
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(text);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const autoResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
  };

  const startNewChat = () => { setActiveSession(null); setMessages([]); setInput(''); };

  return (
    <div className="min-h-screen bg-white flex">
      <AdminNavigation />
      <div className="flex-1 flex overflow-hidden" style={{ height: '100vh' }}>

        {/* Chat sessions sidebar */}
        {chatSidebarOpen && (
          <div className="w-64 bg-slate-50 border-r border-slate-200 flex flex-col shrink-0">
            <div className="p-3 flex items-center gap-2">
              <Button onClick={startNewChat} variant="outline" className="flex-1 h-9 text-sm justify-start gap-2 border-slate-200" data-testid="new-chat-btn">
                <Plus className="w-4 h-4" />Ny samtale
              </Button>
              <Button onClick={() => setChatSidebarOpen(false)} variant="ghost" size="sm" className="h-9 w-9 p-0 text-slate-400">
                <PanelLeftClose className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
              {loadingSessions ? (
                <div className="flex justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
              ) : sessions.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 px-4">Start ein ny samtale for å snakke med AI-assistenten</p>
              ) : sessions.map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveSession(s.id)}
                  className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all text-[13px] ${
                    activeSession === s.id ? 'bg-white shadow-sm text-slate-900 border border-slate-200' : 'hover:bg-white/60 text-slate-600'
                  }`}
                  data-testid={`session-${s.id}`}
                >
                  <MessageSquare className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                  <span className="flex-1 truncate">{s.title}</span>
                  <span
                    onClick={(e) => deleteSession(s.id, e)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-all cursor-pointer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main chat */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Header */}
          <div className="h-12 border-b border-slate-100 flex items-center px-4 gap-3 shrink-0">
            {!chatSidebarOpen && (
              <Button onClick={() => setChatSidebarOpen(true)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400">
                <PanelLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold text-slate-800">Microsafe AI</span>
              <span className="text-[11px] text-slate-400 hidden sm:inline">AI</span>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto px-4 py-8">
              {messages.length === 0 && !sending && (
                <div className="text-center pt-16 pb-8">
                  <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Bot className="w-7 h-7 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Kva kan eg hjelpe med?</h2>
                  <p className="text-sm text-slate-500 mb-8 max-w-sm mx-auto">
                    Eg har tilgang til prøvedataene dine og kan hjelpe med analyse, trendar og tolkingar.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-lg mx-auto">
                    {[
                      { q: 'Kva viser siste prøve?', sub: 'Samandrag av data' },
                      { q: 'Er det kritiske verdiar?', sub: 'Risikovurdering' },
                      { q: 'Forklar alge-trendane', sub: 'Trendanalyse' },
                    ].map(({ q, sub }) => (
                      <button key={q} onClick={() => setInput(q)}
                        className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-left hover:border-teal-300 hover:bg-teal-50/30 transition-all group">
                        <span className="text-sm text-slate-700 group-hover:text-teal-800 block">{q}</span>
                        <span className="text-[11px] text-slate-400">{sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-3 mb-6 ${msg.role === 'user' ? '' : ''}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    msg.role === 'user' ? 'bg-slate-700' : 'bg-teal-500'
                  }`}>
                    {msg.role === 'user' ? <User className="w-3.5 h-3.5 text-white" /> : <Bot className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-500 mb-1">{msg.role === 'user' ? 'Du' : 'Microsafe AI'}</div>
                    <div className="text-[14px] leading-relaxed text-slate-800">
                      {msg.role === 'assistant' ? renderMarkdown(msg.content) : <span className="whitespace-pre-wrap">{msg.content}</span>}
                    </div>
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex gap-3 mb-6">
                  <div className="w-7 h-7 bg-teal-500 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-slate-500 mb-1">Microsafe AI</div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input area */}
          <div className="border-t border-slate-100 bg-white px-4 py-3 shrink-0">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus-within:border-teal-400 focus-within:ring-1 focus-within:ring-teal-400/20 transition-all">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => { setInput(e.target.value); autoResize(e); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Skriv ei melding..."
                  rows={1}
                  className="flex-1 bg-transparent border-0 outline-none resize-none text-sm text-slate-800 placeholder:text-slate-400 min-h-[24px] max-h-[150px] py-1"
                  disabled={sending}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  size="sm"
                  className={`h-8 w-8 p-0 rounded-lg shrink-0 transition-all ${
                    input.trim() ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-slate-200 text-slate-400'
                  }`}
                  data-testid="send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-2">Microsafe AI kan gjere feil. Verifiser viktige data.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
